import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PencilSimple, Trash, ArrowsDownUp, ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { PageHeader, Kpi, Th, Td, Empty, Tag, Confirm } from "@/components/common";
import ProductDialog from "@/components/ProductDialog";
import StockDialog from "@/components/StockDialog";
import ProductVideo from "@/components/ProductVideo";
import ProductPhoto from "@/components/ProductPhoto";
import Attachments from "@/components/Attachments";
import { api, money, qty, fmtDate, L, errMsg } from "@/lib/api";
import { useLoad } from "@/lib/useLoad";

const refLink = (m) => (m.type === "sale" || m.type === "purchase" ? `/faturalar/${m.ref_id}` : null);

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [edit, setEdit] = useState(false);
  const [adj, setAdj] = useState(false);
  const [p, reloadP] = useLoad(() => api.get(`/products/${id}`).then((r) => r.data), [id]);
  const [moves, reloadM] = useLoad(() => api.get(`/products/${id}/movements`).then((r) => r.data), [id]);
  const reload = () => { reloadP(); reloadM(); };
  const del = async () => {
    try {
      await api.delete(`/products/${id}`);
      toast.success("Ürün silindi");
      nav("/stok");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  if (!p) return <div className="label-eyebrow">Yükleniyor…</div>;
  const inSum = (moves || []).filter((m) => m.qty > 0).reduce((s, m) => s + m.qty, 0);
  const outSum = (moves || []).filter((m) => m.qty < 0).reduce((s, m) => s - m.qty, 0);

  return (
    <div>
      <Link to="/stok" className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="back-to-products"><ArrowLeft size={14} /> Stok</Link>
      <PageHeader eyebrow={`${p.code} · ${p.category || "Kategorisiz"}`} title={p.name} testid="product-detail-name">
        <Button variant="outline" className="rounded-[4px]" onClick={() => setAdj(true)} data-testid="product-adjust-button"><ArrowsDownUp /> Stok Giriş / Çıkış</Button>
        <Button variant="ghost" size="icon" onClick={() => setEdit(true)} data-testid="product-edit-button"><PencilSimple /></Button>
        <Confirm title="Ürün silinsin mi?" description="Fatura veya siparişte kullanılan ürünler silinemez." onConfirm={del} testid="product-delete-confirm"
          trigger={<Button variant="ghost" size="icon" className="text-red-600" data-testid="product-delete-button"><Trash /></Button>} />
      </PageHeader>
      <div className="mb-6"><ProductPhoto product={p} onChanged={reloadP} /></div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-5">
        <Kpi label="Mevcut Stok" value={`${qty(p.stock)} ${p.unit}`} tone={p.stock <= p.min_stock ? "red" : "default"} testid="product-stock" />
        <Kpi label="Toplam Giriş" value={qty(inSum)} tone="green" testid="product-in-total" />
        <Kpi label="Toplam Çıkış" value={qty(outSum)} tone="red" testid="product-out-total" />
        <Kpi label="Alış / Satış" value={`${money(p.purchase_price)}`} sub={`Satış: ${money(p.sale_price)} · KDV %${p.vat_rate}`} testid="product-prices" />
        <Kpi label="Stok Değeri" value={money(p.stock * p.purchase_price)} tone="blue" sub={`Kritik seviye: ${qty(p.min_stock)}`} testid="product-value" />
      </div>
      <div className="panel">
        <div className="border-b border-border px-5 py-3.5"><h3 className="font-display text-lg font-bold">Stok Hareketleri</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="product-movements-table">
            <thead><tr><Th>Tarih</Th><Th>Hareket</Th><Th>Belge</Th><Th>Açıklama</Th><Th right>Birim Fiyat</Th><Th right>Giriş</Th><Th right>Çıkış</Th></tr></thead>
            <tbody>
              {moves && moves.length === 0 && <Empty cols={7} />}
              {moves?.map((m) => (
                <tr key={m.id} className="hover:bg-muted/40">
                  <Td className="font-num text-xs">{fmtDate(m.date)}</Td>
                  <Td><Tag tone={m.qty > 0 ? "collection" : "payment"}>{L.stockMove[m.type]}</Tag></Td>
                  <Td className="font-num text-xs">{refLink(m) ? <Link to={refLink(m)} className="text-primary hover:underline">{m.ref_no}</Link> : m.ref_no}</Td>
                  <Td className="text-muted-foreground">{m.description}</Td>
                  <Td right>{money(m.unit_price)}</Td>
                  <Td right className="text-emerald-700">{m.qty > 0 ? qty(m.qty) : ""}</Td>
                  <Td right className="text-red-600">{m.qty < 0 ? qty(-m.qty) : ""}</Td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="mt-6"><ProductVideo product={p} /></div>
      <div className="mt-6"><Attachments entityType="product" entityId={p.id} title="Ürün Belgeleri" /></div>
      <ProductDialog open={edit} onOpenChange={setEdit} initial={p} onSaved={reload} />      <StockDialog open={adj} onOpenChange={setAdj} product={p} onSaved={reload} />
    </div>
  );
}
